"use client";

import React, { useEffect, useState, useMemo } from "react";
import { useTheme } from "next-themes";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import { NavBtn } from "../navButton/NavBtn";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { TooltipItem } from "chart.js";
import "swiper/css";
import "swiper/css/navigation";
import "./style.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
);

// ── matches actual CoinGecko /coins/:id response ──────────────────────────────
interface CoinData {
  id: string;
  name: string;
  symbol: string;
  image: { small: string; thumb: string };
  market_data: {
    current_price: { usd: number };
    price_change_percentage_24h: number;
    market_cap: { usd: number };
    sparkline_7d: { price: number[] }; // ← correct key from CoinGecko
  };
}

const COINS = ["bitcoin", "ethereum", "cardano", "polkadot"];

const CryptoSlider = () => {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [coinsData, setCoinsData] = useState<CoinData[]>([]);
  const [loading, setLoading] = useState(true);

  const isDark = mounted ? resolvedTheme === "dark" : false;

  const bg = isDark ? "#1e1e20" : "#f8f8f8";
  const cardBg = isDark ? "#2b2b2b" : "#f8f8f8";
  const text = isDark ? "#ffffff" : "#111111";
  const muted = isDark ? "#888884" : "#666666";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";

  const [hoverPrev, setHoverPrev] = useState(false);
  const [hoverNext, setHoverNext] = useState(false);

  useEffect(() => {
    setMounted(true);

    const fetchCoinsData = async () => {
      setLoading(true);
      try {
        const responses = await Promise.all(
          COINS.map((coin) =>
            fetch(`/api/crypto?coin=${coin}`).then((r) => r.json()),
          ),
        );
        // filter out error responses
        setCoinsData(responses.filter((r) => r?.market_data));
      } catch (err) {
        console.error("CryptoSlider fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCoinsData();

    const interval = setInterval(fetchCoinsData, 60_000);
    return () => clearInterval(interval);
  }, []);

  // ── Precompute chart datasets ─────────────────────────────────────────────
  const chartDataArray = useMemo(() => {
    return coinsData.map((coin) => {
      // ✅ correct path: market_data.sparkline_7d.price
      const sparkline = coin.market_data?.sparkline_7d?.price ?? [];
      const positive = coin.market_data?.price_change_percentage_24h >= 0;
      const color = positive ? "#16a34a" : "#ef4444";

      return {
        id: coin.id,
        hasData: sparkline.length > 1,
        chartData: {
          labels: sparkline.map((_, i) => i),
          datasets: [
            {
              data: sparkline,
              borderColor: color,
              backgroundColor: positive
                ? "rgba(22,163,74,0.1)"
                : "rgba(239,68,68,0.1)",
              fill: true,
              tension: 0.4,
              pointRadius: 0,
              borderWidth: 1.5,
            },
          ],
        },
      };
    });
  }, [coinsData]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 400 },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: "#111",
          titleColor: "#fff",
          bodyColor: "#fff",
          displayColors: false,
          callbacks: {
            title: (items: TooltipItem<"line">[]) => {
              const index = items[0].dataIndex;

              const date = new Date();
              date.setHours(
                date.getHours() - (items[0].dataset.data.length - index),
              );

              return date.toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
                timeZoneName: "short",
              });
            },

            label: (item: TooltipItem<"line">) => {
              return `Price: $${Number(item.raw).toFixed(4)}`;
            },
          },
        },
      },
      scales: {
        x: { display: false },
        y: { display: false },
      },
      elements: { line: { borderWidth: 1.5 } },
    }),
    [],
  );

  if (!mounted) return null;

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ background: bg, borderRadius: "12px", padding: "12px" }}>
        <style>{`
          @keyframes shimmer {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0.35; }
          }
        `}</style>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              height: "120px",
              borderRadius: "10px",
              background: cardBg,
              marginBottom: "8px",
              animation: `shimmer 1.4s ease-in-out ${i * 0.15}s infinite`,
            }}
          />
        ))}
      </div>
    );
  }

  if (!coinsData.length) {
    return (
      <div
        style={{
          background: bg,
          borderRadius: "12px",
          padding: "16px",
          color: muted,
          fontSize: "13px",
          textAlign: "center",
        }}
      >
        Could not load crypto data.
      </div>
    );
  }

  return (
    <div
      className="crypto-slider-container rounded-3 "
      style={{
        background: bg,
        padding: 0,
        border: `1px solid ${border}`,
        position: "relative",
        boxShadow: isDark
          ? "0 4px 20px rgba(0,0,0,0.6)"
          : "0 4px 20px rgba(0,0,0,0.08)",
        color: text,
        height: "310px",
      }}
    >
      <Swiper
        modules={[Navigation, Autoplay]}
        slidesPerView={1}
        loop
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        navigation={{
          nextEl: ".featured-next-btn",
          prevEl: ".featured-prev-btn",
        }}
        onInit={(swiper) => {
          // @ts-ignore
          swiper.params.navigation.prevEl = ".featured-prev-btn";
          // @ts-ignore
          swiper.params.navigation.nextEl = ".featured-next-btn";

          swiper.navigation.init();
          swiper.navigation.update();
        }}
      >
        {coinsData.map((coin) => {
          const positive = coin.market_data.price_change_percentage_24h >= 0;
          const change =
            coin.market_data.price_change_percentage_24h.toFixed(2);
          const chart = chartDataArray.find((c) => c.id === coin.id);
          const mcap = coin.market_data.market_cap?.usd ?? 0;

          return (
            <SwiperSlide key={coin.id}>
              <div
                style={{
                  background: cardBg,
                  border: `1px solid ${border}`,
                  color: text,
                }}
                className="rounded-3"
              >
                {/* ── Header row ─────────────────────────────────────────── */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0px",
                      padding: "20px 0 0px 20px",
                    }}
                  >
                    {coin.image?.small && (
                      <img
                        src={coin.image.small}
                        alt={coin.name}
                        width={30}
                        height={30}
                        style={{ borderRadius: "50%" }}
                      />
                    )}
                    <div className="ps-2">
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 600,
                          color: text,
                        }}
                      >
                        {coin.name}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: muted,
                          textTransform: "uppercase",
                        }}
                      >
                        {coin.symbol} / USD
                      </div>
                    </div>
                  </div>

                  {/* 24h badge */}
                </div>

                {/* ── Price ──────────────────────────────────────────────── */}
                <div style={{ marginTop: "20px" }}>
                  <div
                    style={{
                      fontSize: "30px",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "0 0 0 30px",
                    }}
                  >
                    $
                    {coin.market_data.current_price.usd.toLocaleString(
                      "en-US",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 4,
                      },
                    )}
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: positive ? "#16a34a" : "#ef4444",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {positive ? "▲" : "▼"} {change}%
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    fontSize: "11px",
                    color: muted,
                    marginBottom: "12px",
                    padding: "0 0 0 30px",
                  }}
                >
                  MCap: $
                  {mcap >= 1e9
                    ? (mcap / 1e9).toFixed(1) + "B"
                    : (mcap / 1e6).toFixed(0) + "M"}
                  &nbsp;·&nbsp;7d chart
                </div>

                {/* ── Sparkline chart ─────────────────────────────────────── */}
                <div style={{ height: "155px", width: "100%" }}>
                  {chart?.hasData ? (
                    <>
                      <div
                        style={{
                          height: "155px",
                          width: "100%",
                          position: "relative",
                        }}
                      >
                        <Line data={chart.chartData} options={chartOptions} />

                        <div
                          style={{
                            position: "absolute",
                            bottom: "4px",
                            right: "6px",
                            fontSize: "10px",
                            color: muted,
                            opacity: 0.6,
                          }}
                        >
                          Auto-refresh 60s · CoinGecko
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        height: "125px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: muted,
                        fontSize: "12px",
                      }}
                    >
                      No chart data
                    </div>
                  )}
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {/* LEFT BUTTON */}
      <NavBtn
        direction="prev"
        hover={hoverPrev}
        className="featured-prev-btn swiper-nav-btn"
        onMouseEnter={() => setHoverPrev(true)}
        onMouseLeave={() => setHoverPrev(false)}
      />

      <NavBtn
        direction="next"
        hover={hoverNext}
        className="featured-next-btn swiper-nav-btn"
        onMouseEnter={() => setHoverNext(true)}
        onMouseLeave={() => setHoverNext(false)}
      />
    </div>
  );
};

export default CryptoSlider;
